//Add Edit event schema with yup and reacthook form

import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  Typography
} from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import PrimaryButton from '../ui/buttons/primaryButton';
import eventService from '../../features/event/eventService';

import Input from '../ui/forms/fields/input';
import Select from '../ui/forms/fields/select';

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  rounds: yup.number().default(1).required('Rounds is required.'),
  order: yup.number().required('Order is required.'),
  winnerPlaysSection: yup.string()
});

const AddEditSectionDialog = ({ open, onClose, event, section }) => {
  const dispatch = useDispatch();

  const {
    register,
    handleSubmit,
    setValue,
    control,
    reset,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: section
  });

  const [saving, setSaving] = useState(false);

  const [sectionGames, setSectionGames] = useState([]);

  const sections = event.sections;

  const getNextSectionOrder = () => {
    const existingOrders = Array.isArray(event.sections)
      ? event.sections.map((section) => Number(section.order || 0))
      : [];
    const maxOrder = existingOrders.length > 0 ? Math.max(...existingOrders) : 0;
    return maxOrder + 1;
  };

  const games = useSelector((state) => state.game.games);

  useEffect(() => {
    if (section) {
      setValue('name', section.name);
      setValue('rounds', section.rounds);
      setValue('order', section.order);
      setValue('winnerPlaysSection', section.winnerPlaysSection ? section.winnerPlaysSection : '');
    } else if (open && event) {
      setValue('rounds', event.gameFormat.rounds);
      setValue('order', getNextSectionOrder(), { shouldValidate: true });
    }
  }, [section, setValue, open, event]);

  useEffect(() => {
    if (!games || !section) return;

    const gamesInSection = games.filter((g) => g.section === section.id);
    setSectionGames(gamesInSection);
  }, [games, section, event]);

  useEffect(() => {
    return () => {
      reset();
    };
  }, [reset]);

  const onSubmit = async (data) => {
    setSaving(true);
    if (event && section) {
      await eventService.updateSection(event.id, { ...data, id: section.id });
    } else if (event) {
      await eventService.addSection(event.id, data);
    }

    setSaving(false);
    onClose();
  };

  const handleAddEdit = () => {
    handleSubmit(onSubmit)();
  };

  const handleAutoGenerateName = () => {
    const order = getNextSectionOrder();
    const name = `Section ${order}`;
    setValue('name', name, { shouldValidate: true, shouldDirty: true });
    setValue('order', order, { shouldValidate: true, shouldDirty: true });
  };

  // Delete handler

  return (
    <Fragment>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
        <DialogTitle>
          <Typography variant="h4" component="h1">
            {section ? 'Edit Section' : 'New Section'}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box display={'flex'} flexDirection={'row'} gap={2}>
            <Stack flexGrow={1}>
              <Box display="flex" flexDirection={'row'} alignItems="center">
                <Input
                  label="Name"
                  {...register('name')}
                  error={errors.name}
                  disabled={saving}
                  focused={true}
                />

                {!section && (
                  <PrimaryButton
                    variant="contained"
                    color="secondary"
                    startIcon={<AutoFixHighIcon fontSize="small" />}
                    sx={{ ml: 1, minWidth: '190px', height: '48px', fontWeight: 'bold' }}
                    onClick={() => handleAutoGenerateName()}>
                    Generate Section
                  </PrimaryButton>
                )}
              </Box>

              {!section && (
                <Input
                  label="Rounds"
                  {...register('rounds')}
                  error={errors.rounds}
                  disabled={saving}
                  defaultValue={event.gameFormat.rounds}
                  type={event.gameFormat.rounds > 0 ? 'number' : 'hidden'}
                />
              )}

              {sections && sections.length > 0 && (
                <Select name="winnerPlaysSection" label="Winner Plays Section" control={control}>
                  <MenuItem value={''}>None</MenuItem>
                  {sections.map((section) => (
                    <MenuItem key={section.id} value={section.id}>
                      {section.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
              <Input
                label="Order"
                type="number"
                error={errors.order}
                placeholder={'Order of Sections'}
                {...register('order')}
              />
            </Stack>
          </Box>
        </DialogContent>
        <DialogActions>
          {saving ? (
            <CircularProgress />
          ) : (
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'flex-end'}
              width={'100%'}
              gap={1}>
              {section?.finalised && (
                <Typography variant="caption" color="success">
                  Section was won by {section.winner.teamName}
                </Typography>
              )}

              <Box
                display={'flex'}
                flexDirection={'row'}
                gap={1}
                justifyContent={'flex-end'}
                width={'100%'}>
                <PrimaryButton variant={'outlined'} color={'error'} onClick={onClose}>
                  Cancel
                </PrimaryButton>
                <PrimaryButton variant={'contained'} onClick={() => handleAddEdit()}>
                  {section ? 'Update' : 'Add'}
                </PrimaryButton>
              </Box>
            </Box>
          )}
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default AddEditSectionDialog;
